import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import OtherActionsDoneButton from './buttons/otherActionsDoneButton';
import DownloadFullReportDialog from './downloadReport/downloadFullReportDialog';
import CustomerFinalReport from './finalReport/CustomerFinalReport';
import {
  transformFieldValueForAPI,
  transformNewFieldValueForAPI,
} from './helper';
import MainActions from './reportActions/mainActions';
import CaseIdentifier from './reportActions/otherActions/caseIdentifier';
import InterviewPlannerButton from './reportActions/otherActions/interviewPlanner';
import OtherActionsButton from './reportActions/otherActions/otherActionsButton';
import PiiRemovalRequestButton from './reportActions/otherActions/piiRemovalRequest';
import CustomerToggleLockReport from './toggle/CustomerToggleLockReport';
import CustomerToggleWhistleBlowerFeedback from './toggle/CustomerToggleWhistleBlowerFeedback';
import UpdateTextBoxButton from './UpdateTextBox';

import archiveIcon from '../../../assets/svg/archiveIcon.svg';
import piiRemovalIcon from '../../../assets/svg/piiRemovalIcon.svg';
import redactInformationIcon from '../../../assets/svg/redactInformationIcon.svg';
import unarchiveIcon from '../../../assets/svg/unarchiveIcon.svg';
import Union from '../../../assets/svg/Union.svg';
import { axiosInstanceForTenantAPI } from '../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../assets/utils/helpers';
import {
  CLOSED_REPORTS_STATUS_BOUNDARY,
  Status,
} from '../../../constants/reportConstants';
import { useAuth } from '../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import { isExperimental } from '../../../hooks/global/useExperimental';
import ErrorType from '../../../interfaces/ErrorType';
import { ReportActionsDataProps } from '../../../interfaces/reportActions';
import { UserAccessRoles } from '../../../interfaces/ReportPageAccessRoles';
import RESTAPIError from '../../../interfaces/RESTAPIError';
import customerAuthService from '../../../services/customerAuthService';
import {
  clearPiiRemovedMessageRanges,
  clearPiiRemovedDescriptionPositions,
  setPiiRemovalFlag,
  setRemoveAllAttachmentsFlag,
  setRemoveCountryFlag,
  setRemoveReporterFlag,
} from '../../../slices/piiRemovalSlice';
import {
  revertInitialRedactedDescriptionPositions,
  setRedactInfoState,
  setReport,
} from '../../../slices/reportDataSlice';
import { RootState } from '../../../utils/store';
import ConfirmDialog from '../../common/dialog/confirmDialog';
import SnackBarErrorResponse from '../../common/snackBarErrorResponse';
// import ReportCommentsDrawer from '../reports/reportActions/comments/reportCommentsDrawer';

import { Button as NewButton } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import CreateMeeting from '~/pages/customer/reports/report-details/create-meeting/CreateMeeting';
import { PotentialReportLinksAlert } from '~/pages/customer/reports/report-details/PotentialReportLinks';
import ExpandedCaseAction from '~/pages/customer/reports/report-details/report-actions/ExpandedCaseAction';
import { ReportComments } from '~/pages/customer/reports/report-details/report-comments/ReportComments';

// import SetupMeeting from './reportActions/otherActions/setupMeeting';

// TODO: remove `id` here once BE can handle linking using `reference_id`

interface ReportActionsProps {
  mutate: () => void;
}

const CustomerReportActions = ({ mutate }: ReportActionsProps) => {
  const STATUS_UNDER_REVIEW = 2;
  const STATUS_CLOSED_RESOLVED = '3';
  const STATUS_CLOSED_INCOMPLETE_INFO = '4';
  const STATUS_CLOSED_WHISTLEBLOWER = '5';

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { createToastNotification } = useToastNotificationContext();

  const { slug } = useAuth();
  const intl = useIntl();
  const dispatch = useDispatch();

  const piiRemovalRequestData = useSelector(
    (state: RootState) => state.piiRemoval.piiRemovalRequestData,
  );

  const reportData = useSelector((state: RootState) => state.reportData.report);
  const {
    is_allowed_to_reopen_report: reopenReportFlag,
    is_allowed_to_redact_report: allowedToRedactFlag,
    is_read_only_report: isReadOnlyReport,
  } = useSelector((state: RootState) => state.reportData.userAccessRoles);

  const redactInfoState = useSelector(
    (state: RootState) => state.reportData.redactInfoState,
  );

  const { piiRemovalFlag, viewPiiRequest } = useSelector(
    (state: RootState) => state.piiRemoval,
  );

  useEffect(() => {
    setOpenOtherActions(viewPiiRequest);
  }, [viewPiiRequest]);

  const availableActions = useSelector(
    (state: RootState) => state.reportAction.availableActions,
  );

  const isDetailsTranslated = useSelector(
    (state: RootState) => state.reportData.isDetailsTranslated,
  );
  const translateIsLoading = useSelector(
    (state: RootState) => state.reportData.translateIsLoading,
  );

  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const [confirmSubmissionOpen, setConfirmSubmissionOpen] =
    useState<boolean>(false);
  const [openOtherActions, setOpenOtherActions] = useState(false);
  const [reportCaseStatus, setReportCaseStatus] = useState(reportData.status);
  const [userAccessRoles, setUserAccessRoles] = useState<UserAccessRoles>();
  const [disabledFlag, setDisableFlag] = useState<boolean>(true);
  const [reportActionData, setReportActionData] =
    useState<ReportActionsDataProps>({
      assignee: reportData.assignee,
      followers: reportData.followers,
      priority: reportData.priority,
      status: `${reportData.status}`,
      department: `${
        reportData.department === null ? '' : reportData.department.id
      }`,
      subjectIndividual: reportData.subjectIndividual ?? '',
      suspectedDepartment: reportData.suspectedDepartment ?? '',
      selectedTags: reportData.tags,
      reportType: `${reportData.reportType && reportData.reportType.id}`,
      reporter: reportData.reporter ?? '',
      linkedReport: reportData.linkedReports,
      dueDate: new Date(reportData.firstDueDate ?? ''),
      nextReview: reportData.nextReview
        ? new Date(reportData.nextReview)
        : undefined,
      reportActionFieldValues: reportData.reportActionFieldValues,
      employeeSanction: reportData.employeeSanction ?? '',
      lossValue: reportData.lossValue ?? '',
      lossValueCurrency: reportData.lossValueCurrency ?? '',
      reportOutcome: reportData.reportOutcome ?? '',
    });
  const [error, setError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });

  useEffect(() => {
    setDisableFlag(
      isReadOnlyReport || reportData.status > CLOSED_REPORTS_STATUS_BOUNDARY,
    );
  }, [reportData, isReadOnlyReport]);

  useEffect(() => {
    setReportActionData({
      assignee: reportData.assignee,
      followers: reportData.followers,
      priority: reportData.priority,
      status: `${reportData.status}`,
      department: `${
        reportData.department === null ? '' : reportData.department.id
      }`,
      subjectIndividual: reportData.subjectIndividual,
      suspectedDepartment: reportData.suspectedDepartment,
      selectedTags: reportData.tags,
      reportType: `${reportData.reportType && reportData.reportType.id}`,
      reporter: reportData.reporter ?? '',
      linkedReport: reportData.linkedReports,
      dueDate: new Date(reportData.firstDueDate ?? ''),
      nextReview: reportData.nextReview
        ? new Date(reportData.nextReview)
        : undefined,
      reportActionFieldValues: reportData.reportActionFieldValues,
      employeeSanction: reportData.employeeSanction,
      lossValue: reportData.lossValue,
      lossValueCurrency: reportData.lossValueCurrency,
      reportOutcome: reportData.reportOutcome,
    });
    setReportCaseStatus(reportData.status);
  }, [reportData]);

  const fetchAccessRoles = useCallback(async () => {
    try {
      const response = await customerAuthService.getAccessRoles();
      setUserAccessRoles(response);
    } catch (returnError) {
      const APIError = returnError as RESTAPIError;
      alert(APIError);
    }
  }, []);

  useEffect(() => {
    fetchAccessRoles();
  }, [fetchAccessRoles]);

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const updateTenantReport = async (data: ReportActionsDataProps) => {
    setError({
      errorType: false,
      message: '',
    });
    setIsLoading(true);

    if (disabledFlag == true && reopenReportFlag == true) {
      // This should use the endpoint for reopening close/archived reports instead
      try {
        await axiosInstanceForTenantAPI(companySlug).patch(
          `/reopen-closed-report/${reportData.referenceId}`,
          { status: STATUS_UNDER_REVIEW },
        );
      } catch (err) {
        const APIError = err as RESTAPIError;

        alert('Error: ' + APIError);
      }
    }

    try {
      const newdata = {
        assignee: data.assignee,
        followers: data.followers,
        priority: data.priority,
        tags: data.selectedTags,
        reporter: data.reporter,
        status: data.status,
        department: data.department,
        subject_individual: data.subjectIndividual,
        suspected_department: data.suspectedDepartment,
        report_type: data.reportType,
        linked_reports: data.linkedReport.map((report) => report.referenceId),
        due_date: data.dueDate,
        next_review_date: data.nextReview,
        ...(data.reportOutcome && { report_outcome: data.reportOutcome }),
        ...(data.employeeSanction && {
          employee_sanction: data.employeeSanction,
        }),
        ...(data.lossValue && { loss_value: data.lossValue }),
        ...(data.lossValueCurrency && {
          loss_value_currency: data.lossValueCurrency,
        }),
        ...(data.reportActionFieldValues.length > 0 && {
          case_manager_fields_value: transformFieldValueForAPI(
            data.reportActionFieldValues,
            availableActions ? availableActions.map((action) => action.id) : [],
          ),
        }),
        ...(data.reportActionFieldValues.length > 0 && {
          new_case_manager_field_value: transformNewFieldValueForAPI(
            data.reportActionFieldValues,
          ),
        }),
      };
      const response = await axiosInstanceForTenantAPI(companySlug).patch(
        `/report/update/${reportData.referenceId}`,
        newdata,
      );
      mutate();
      setIsLoading(false);
      setConfirmSubmissionOpen(false);
      createToastNotification(
        intl.formatMessage({ defaultMessage: 'Report actions updated.' }),
        'success',
      );
      const res = response.data;
      return res;
    } catch (err: unknown) {
      let errorMessage = intl.formatMessage({
        // eslint-disable-next-line quotes
        defaultMessage: "Error happened. We're currently fixing it.",
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            // eslint-disable-next-line quotes
            defaultMessage: "Error happened, We're currently fixing it.",
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Failure to process request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You are unauthorized to update report',
          });
        } else if (`${err.response.status}` === String(StatusCodes.FORBIDDEN)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'You cannot use this request',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setError({
        errorType: true,
        message: errorMessage,
      });
      setIsLoading(false);
      setConfirmSubmissionOpen(false);
    }
  };

  const handleCancelConfirmation = () => {
    setConfirmSubmissionOpen(false);
  };

  const handleCloseReportConfirmation = () => {
    updateTenantReport(reportActionData);
  };

  const handleSubmitReportAction = () => {
    if (
      reportActionData.status === STATUS_CLOSED_RESOLVED ||
      reportActionData.status === STATUS_CLOSED_INCOMPLETE_INFO ||
      reportActionData.status === STATUS_CLOSED_WHISTLEBLOWER
    ) {
      if (
        `${status}` === STATUS_CLOSED_RESOLVED ||
        `${status}` === STATUS_CLOSED_INCOMPLETE_INFO ||
        `${status}` === STATUS_CLOSED_WHISTLEBLOWER
      ) {
        return;
      }
      setConfirmSubmissionOpen(true);
      return setIsLoading(false);
    }

    if (
      reportActionData.status !== STATUS_CLOSED_RESOLVED &&
      reportActionData.status !== STATUS_CLOSED_INCOMPLETE_INFO &&
      reportActionData.status !== STATUS_CLOSED_WHISTLEBLOWER
    ) {
      updateTenantReport(reportActionData);
    }
  };

  const handleCloseSnackbar = (reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setError((prevError) => ({ ...prevError, errorType: false }));
  };

  const allowReopenReport = () => {
    if (
      disabledFlag == true &&
      reopenReportFlag == true &&
      reportData.status !== 6
    ) {
      return false;
    } else if (
      disabledFlag == false &&
      reopenReportFlag == true &&
      reportData.status !== 6
    ) {
      return false;
    } else if (disabledFlag == true && reopenReportFlag == false) {
      return true;
    } else if (reopenReportFlag == false && disabledFlag == false) {
      return false;
    }
    return true;
  };

  const isDisabled = allowReopenReport();

  const handleClick = () => {
    setOpenOtherActions(!openOtherActions);
  };

  const handleChangeToUnarchived = async () => {
    if (userAccessRoles?.is_read_only_report) {
      setError({
        errorType: true,
        message: intl.formatMessage({
          defaultMessage:
            'You are only allowed to view this report and you cannot make any changes to it.',
        }),
        severity: 'info',
      });
    } else {
      setIsLoading(true);
      try {
        const body = {
          status: 1,
        };

        const response = await axiosInstanceForTenantAPI(companySlug).patch(
          `/reopen-closed-report/${reportData.referenceId}`,
          body,
        );
        setError({
          errorType: false,
          message: '',
        });

        if (response) {
          setIsLoading(false);
          setReportCaseStatus(Status.PENDING_FURTHER_INFORMATION);
          dispatch(
            setReport({
              ...reportData,
              status: Status.PENDING_FURTHER_INFORMATION,
            }),
          );
          createToastNotification('Report successfully unarchived.', 'success');
          mutate();
        }
      } catch (err: unknown) {
        let errorMessage = intl.formatMessage({
          // eslint-disable-next-line quotes
          defaultMessage: "Error happened. We're currently fixing it.",
        });
        if (isRESTAPIError(err)) {
          if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
            errorMessage = intl.formatMessage({
              // eslint-disable-next-line quotes
              defaultMessage: "Error happened. We're currently fixing it.",
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Failure to process request',
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'You are unauthorized to update report',
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.FORBIDDEN)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage:
                'You are not permitted to reopen or unarchive this report',
            });
          } else if (
            `${err.response.status}` ===
            String(StatusCodes.INTERNAL_SERVER_ERROR)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Error happened in the server',
            });
          }
        }
        setError({
          errorType: true,
          message: errorMessage,
        });
        setIsLoading(false);
      }
    }
  };

  const handleToggleAutoResponse = async () => {
    if (userAccessRoles?.is_read_only_report) {
      setError({
        errorType: true,
        message: intl.formatMessage({
          defaultMessage:
            'You are only allowed to view this report and you cannot make any changes to it.',
        }),
        severity: 'info',
      });
    } else {
      setIsLoading(true);
      try {
        const body = {
          status: 6,
        };
        const response = await axiosInstanceForTenantAPI(companySlug).patch(
          `/report/update/${reportData.referenceId}`,
          body,
        );
        setError({
          errorType: false,
          message: '',
        });
        if (response) {
          setReportCaseStatus(Status.ARCHIVED);
          dispatch(setReport({ ...reportData, status: Status.ARCHIVED }));
          setIsLoading(false);
          createToastNotification(
            'Report successfully moved to archive.',
            'success',
          );
          await new Promise((resolve) => setTimeout(resolve, 1000));

          mutate();
        }
      } catch (err: unknown) {
        let errorMessage = intl.formatMessage({
          // eslint-disable-next-line quotes
          defaultMessage: "Error happened. We're currently fixing it.",
        });
        if (isRESTAPIError(err)) {
          if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
            errorMessage = intl.formatMessage({
              // eslint-disable-next-line quotes
              defaultMessage: "Error happened. We're currently fixing it.",
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Failure to process request',
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'You are unauthorized to update report',
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.FORBIDDEN)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage:
                'You are not permitted to reopen or unarchive this report',
            });
          } else if (
            `${err.response.status}` ===
            String(StatusCodes.INTERNAL_SERVER_ERROR)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Error happened in the server',
            });
          }
        }
        setError({
          errorType: true,
          message: errorMessage,
        });
        setIsLoading(false);
      }
    }
  };

  const handleRedactInfoClick = () => {
    dispatch(setRedactInfoState(!redactInfoState)); // Toggle the visibility state of the Redact information's "done" button
  };

  const handleCancelRedactButtonClick = () => {
    dispatch(setRedactInfoState(false)); // Hide the Redact information's "done" button when cancel button is clicked
    dispatch(revertInitialRedactedDescriptionPositions()); // revert redacted information highlights to initial state
  };

  const handlePiiRemovalClick = () => {
    // Toggle the visibility state of the PII removal's "done" button
    dispatch(setPiiRemovalFlag(true));
  };

  const handleCancelPiiRemovalClick = () => {
    // Hide the PII removal's "done" button when cancel button is clicked
    dispatch(setPiiRemovalFlag(false));
    dispatch(setRemoveCountryFlag(false));
    dispatch(setRemoveReporterFlag(false));
    dispatch(setRemoveAllAttachmentsFlag(false));
    dispatch(clearPiiRemovedMessageRanges());
    dispatch(clearPiiRemovedDescriptionPositions());
  };

  return (
    <Stack
      sx={{
        border: '1px solid #D6D6D6',
        borderRadius: '6px',
        width: '100%',
        minWidth: '320px',
        flex: '1',
        overflow: 'hidden',
      }}
      bgcolor='#FFF'
    >
      <SnackBarErrorResponse
        open={error.errorType}
        message={error.message}
        handleCloseSnackbar={handleCloseSnackbar}
        severity={error.severity ?? 'error'}
      />
      <Card
        sx={{
          padding: '12px',
          background: `url(${Union}) no-repeat right top / 60% 100% content-box,
          linear-gradient(270deg, #00CECF 0%, #2449FC 100%)`,
          flexBasis: 100,
          borderRadius: '6px 6px 0 0',
        }}
      >
        <div className='flex flex-col justify-center gap-2'>
          <div className='flex justify-between'>
            <NewButton
              variant='plain'
              color='white'
              size='sm'
              className='hover:!bg-sky-700'
              onClick={handleClick}
              startDecorator={openOtherActions ? <ArrowLeft /> : <></>}
              endDecorator={openOtherActions ? <></> : <ArrowRight />}
            >
              {openOtherActions ? 'Actions' : 'Other Actions'}
            </NewButton>
            <ExpandedCaseAction
              reportActionData={reportActionData}
              setReportActionData={setReportActionData}
              disabledFlag={disabledFlag}
              triggerUpdate={() => {
                if (isExperimental) handleSubmitReportAction();
                else updateTenantReport(reportActionData);
              }}
              isLoading={isLoading}
            />
          </div>
          <div className='flex justify-end'>
            {!openOtherActions && !piiRemovalFlag && !redactInfoState && (
              <NewButton
                color='white'
                className='!text-blue-600'
                onClick={
                  isExperimental
                    ? handleSubmitReportAction
                    : () => updateTenantReport(reportActionData)
                }
                disabled={isDisabled || isLoading}
                loading={isLoading}
              >
                <FormattedMessage defaultMessage='Update' />
              </NewButton>
            )}
          </div>
        </div>
      </Card>

      {openOtherActions ? (
        // OTHER ACTIONS
        <Stack
          spacing={1.5}
          sx={{
            padding: '20px',
            overflow: 'auto',
            '::-webkit-scrollbar': {
              width: '10px',
            },
            '::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(155, 155, 155, 0.5)',
              borderRadius: ' 20px',
              border: ' transparent',
            },
            width: '100%',
            flex: 1,
          }}
        >
          <PotentialReportLinksAlert />
          {/* SETUP MEETING BUTTON */}
          <CreateMeeting
            reportId={reportData.reportId}
            assignee={reportData.assignee}
            conversation={reportData.conversation}
          />
          <Divider
            sx={{
              paddingY: '10px',
              width: '100%',
            }}
          />
          <CaseIdentifier disabledFlag={disabledFlag} />
          <Divider
            sx={{
              paddingY: '10px',
              width: '100%',
            }}
          />
          <CustomerToggleLockReport
            disabledFlag={disabledFlag}
            reportId={reportData.referenceId}
            isCaseLockedEnabled={reportData.isCaseLockedEnabled}
          />
          <hr
            style={{
              border: 'none',
              borderTop: '1px solid rgba(209, 209, 209, 1)',
              width: '280px',
              height: '1px',
              marginTop: '32px',
              marginBottom: '28px',
            }}
          />
          <CustomerToggleWhistleBlowerFeedback
            disabledFlag={
              userAccessRoles?.is_read_only_report ??
              (reportCaseStatus <= CLOSED_REPORTS_STATUS_BOUNDARY
                ? false
                : true)
            }
            reportId={reportData.referenceId}
            isCaseLockedEnabled={reportData.isCaseLockedEnabled}
          />
          <hr
            style={{
              border: 'none',
              borderTop: '1px solid rgba(209, 209, 209, 1)',
              width: '280px',
              height: '1px',
              marginTop: '32px',
              marginBottom: '28px',
            }}
          />
          {/* PII Removal Request Button (Only viewable by admin if PII request is present) */}
          {piiRemovalRequestData?.report_reference ===
          reportData.referenceId ? (
            <PiiRemovalRequestButton refetchData={mutate} />
          ) : (
            ''
          )}
          {/* Archive/Unarchive Buttons */}
          <UpdateTextBoxButton />
          <DownloadFullReportDialog />
          <InterviewPlannerButton />
          {reportCaseStatus === Status.ARCHIVED && (
            <>
              <OtherActionsButton
                label={intl.formatMessage({ defaultMessage: 'Unarchive' })}
                onClick={handleChangeToUnarchived}
                isLoading={isLoading}
                startIcon={
                  <img
                    src={unarchiveIcon}
                    alt='Confide'
                    style={{ height: '20px' }}
                  />
                }
              />

              <Stack
                style={{
                  paddingRight: '25px',
                  width: '100%',
                }}
              ></Stack>
            </>
          )}
          {/* <ReportCommentsDrawer /> */}
          <ReportComments />
          {reportCaseStatus !== Status.ARCHIVED && (
            <OtherActionsButton
              label={intl.formatMessage({ defaultMessage: 'Move to archive' })}
              onClick={handleToggleAutoResponse}
              isLoading={isLoading}
              isDisabled={isLoading ?? piiRemovalFlag ?? redactInfoState}
              startIcon={
                <img
                  src={archiveIcon}
                  alt='Confide'
                  style={{ height: '20px' }}
                />
              }
            />
          )}
          {/*  Redact Information Button*/}
          {redactInfoState ? (
            <OtherActionsDoneButton
              refetchData={mutate}
              buttonFunction={'RedactInformation'}
              onCancel={handleCancelRedactButtonClick}
              slug={companySlug}
              referenceId={reportData.referenceId}
            />
          ) : (
            <OtherActionsButton
              label={intl.formatMessage({
                defaultMessage: 'Redact Information',
              })}
              onClick={handleRedactInfoClick}
              isDisabled={
                disabledFlag ??
                piiRemovalFlag ??
                allowedToRedactFlag ??
                isDetailsTranslated ??
                translateIsLoading ??
                reportData.isDescriptionEditable
              }
              startIcon={
                <img
                  src={redactInformationIcon}
                  alt='redact-info-icon'
                  style={{ height: '20px' }}
                />
              }
            />
          )}
          {/* PII Removal Button */}
          {piiRemovalFlag === true ? (
            <OtherActionsDoneButton
              refetchData={mutate}
              slug={companySlug}
              referenceId={reportData.referenceId}
              buttonFunction={'PIIRemoval'}
              onCancel={handleCancelPiiRemovalClick}
            />
          ) : disabledFlag ||
            redactInfoState === true ||
            isDetailsTranslated ||
            translateIsLoading ||
            piiRemovalRequestData ||
            reportData.isDescriptionEditable ? (
            <Button
              disabled
              sx={{
                display: 'flex',
                height: '40px',
                padding: '8px 12px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                alignSelf: 'stretch',
                backgroundColor: 'rgba(246, 246, 246, 1)',
                color: 'rgba(93, 93, 93, 1)',
                textTransform: 'none',
                marginTop: '400px',
              }}
            >
              <img
                src={piiRemovalIcon}
                alt='pii-removal-icon'
                style={{ height: '20px' }}
              />
              <FormattedMessage defaultMessage='PII removal' />
            </Button>
          ) : (
            <Button
              onClick={handlePiiRemovalClick}
              sx={{
                display: 'flex',
                height: '40px',
                padding: '8px 12px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                alignSelf: 'stretch',
                backgroundColor: 'rgba(246, 246, 246, 1)',
                color: 'rgba(93, 93, 93, 1)',
                textTransform: 'none',
                marginTop: '400px',
              }}
            >
              <img
                src={piiRemovalIcon}
                alt='pii-removal-icon'
                style={{ height: '20px' }}
              />
              <FormattedMessage defaultMessage='PII removal' />
            </Button>
          )}
          {piiRemovalRequestData?.report_reference ===
          reportData.referenceId ? (
            <div className='w-auto flex row'>
              <Typography sx={{ marginLeft: 'auto', color: '#FF9900' }}>
                <FormattedMessage defaultMessage='Pending Request' />
              </Typography>
            </div>
          ) : (
            ''
          )}
        </Stack>
      ) : (
        // REPORT ACTIONS
        <div className='flex flex-col gap-2 p-5 overflow-y-auto flex-1 max-w-80'>
          <MainActions
            reportActionData={reportActionData}
            setReportActionData={setReportActionData}
            disabledFlag={disabledFlag}
          />
          <Divider />
          {/* Final Report */}
          <FinalReportCard />
        </div>
      )}

      {/* TODO: refactor to use `openConfirmationModal` instead of `ConfirmDialog` component */}
      <ConfirmDialog
        proceedButtonContent={intl.formatMessage({
          defaultMessage: 'Close report',
        })}
        dialogContent={intl.formatMessage({
          defaultMessage:
            'Please confirm if you wish to close this report without completing a final report assessment.',
        })}
        dialogTitle={intl.formatMessage({
          defaultMessage: 'Close Report Confirmation',
        })}
        open={confirmSubmissionOpen}
        handleCancel={handleCancelConfirmation}
        handleConfirm={handleCloseReportConfirmation}
        isLoading={isLoading}
      />
    </Stack>
  );
};

export default CustomerReportActions;

function FinalReportCard() {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const userAccessRoles = useSelector(
    (state: RootState) => state.reportData.userAccessRoles,
  );
  const reportDetails = useSelector(
    (state: RootState) => state.reportData.report,
  );

  const handleClick = () => {
    if (userAccessRoles?.is_read_only_report) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You are only allowed to view this report and you cannot make any changes to it.',
        }),
        'info',
      );
    } else if (
      userAccessRoles?.is_allowed_to_reopen_report &&
      !userAccessRoles?.is_read_only_report &&
      reportDetails.status === Status.ARCHIVED
    ) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You need to unarchive a report first before editing.',
        }),
        'info',
      );
    } else if (
      !userAccessRoles?.is_allowed_to_reopen_report === false &&
      userAccessRoles?.is_read_only_report === true &&
      (reportDetails.status === Status.CLOSED_NOT_ENOUGH_INFORMATION ||
        reportDetails.status === Status.CLOSED_RESOLVED ||
        reportDetails.status === Status.CLOSED_WHISTLEBLOWER_INITIATED)
    ) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'Cannot Edit Closed Report, You need to re-open the report first before editing.',
        }),
        'info',
      );
    }
  };

  return (
    <>
      {!userAccessRoles?.is_read_only_report && (
        <Box onClick={handleClick}>
          <CustomerFinalReport />
        </Box>
      )}
    </>
  );
}
